import LocalTaxiIcon from '@mui/icons-material/LocalTaxi';
import PeopleIcon from '@mui/icons-material/People';
import RoomIcon from '@mui/icons-material/Room';
import {
    Avatar,
    Box,
    Button,
    CircularProgress,
    Divider,
    Grid,
    ListItem,
    ListItemAvatar,
    ListItemText,
    Typography,
} from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { fireBookingCreated } from 'analytics/bookingEvents';
import DirectionsMap from 'components/Common/DirectionsMap/DirectionsMap';
import moment from 'moment';
import React from 'react';
import { confirmAlert } from 'react-confirm-alert';
import 'react-confirm-alert/src/react-confirm-alert.css';
import { useSelector } from 'react-redux';
import { bookingsAPI } from '../../../api/bookingsAPI';
import BaseLayout from '../../../layouts/Base/BaseLayout';
import styles from './bookingConfirmationPageStyle';

export function BookingConfirmationPage(props: any) {
  const newBooking = useSelector((state: any) => state.bookings.newBooking)
  const bookingPreview = useSelector(
    (state: any) => state.bookings.newBookingPreview,
  )
  const quotedBooking = useSelector(
    (state: any) => state.bookings.newQuotedBooking,
  )

  const [clientName] = React.useState(
    useSelector((state: any) => state.bookings.bookingClient?.companyName),
  )
  const [vehicleInfo, setVehicleInfo] = React.useState<any>(null)
  const [bookingConfirmation, setBookingConfirmation] =
    React.useState<any>(null)
  const [isLoading, setIsLoading] = React.useState(true)

  const { mutateAsync: cancelBooking } = useMutation({
    mutationFn: (bookingId) => {
      return bookingsAPI.cancelBooking(bookingId)
    },
  })

  const cancelExistingBooking = async (bookingId: any) => {
    return await cancelBooking(bookingId).then((data: any) => {
      if (data.status === 204) {
        props.history.push('/admin/dashboard')
      }
    })
  }

  const { mutateAsync: createBooking } = useMutation({
    mutationFn: (quotedBooking) => {
      return bookingsAPI.createBooking(quotedBooking)
    },
  })

  const createNewBooking = async (quotedBooking: any) => {
    return await createBooking(quotedBooking)
      .then((data: any) => {
        setBookingConfirmation(data.data)
        setIsLoading(false)
        const toNum = (v: any) =>
          typeof v === 'number'
            ? Number(v.toFixed(2))
            : typeof v === 'string' && v.length
            ? Number(parseFloat(v).toFixed(2))
            : undefined
        const full = bookingPreview?.estimatedPriceFull
        const winningPrice = toNum(
          full?.total ?? bookingPreview?.estimatedPrice,
        )
        const winningPriceExVat = toNum(full?.price)
        fireBookingCreated({
          surface: 'quote_new',
          bookingId: data?.data?.id,
          bookingReference: data?.data?.bookingReference,
          quoteGroupId: quotedBooking?.outbound?.quoteReference,
          supplierQuoteReference:
            quotedBooking?.outbound?.vehicles?.[0]?.supplierQuoteReference,
          vehicleType: bookingPreview?.vehicles?.[0]?.vehicleTypeName,
          winningPrice,
          winningPriceExVat,
          currency: full?.currency ?? bookingPreview?.estimatedPriceCurrency,
        })
      })
      .catch((e) => console.log('ERROR ', e))
  }

  const options: any = {
    title: 'Cancel Booking',
    message: 'Are you sure you want to cancel your booking?',
    buttons: [
      {
        label: 'Yes',
        onClick: () => cancelExistingBooking(bookingConfirmation.id),
      },
      {
        label: 'No',
        onClick: () => {},
      },
    ],
    childrenElement: () => <div />,
    closeOnEscape: true,
    closeOnClickOutside: true,
    willUnmount: () => {},
    afterClose: () => {},
    onClickOutside: () => {},
    onKeypressEscape: () => {},
  }

  const cancelMyBooking = async () => {
    confirmAlert(options)
  }

  const getVehicleInfo = () => {
    if (bookingPreview && bookingPreview.vehicles) {
      return `${bookingPreview.vehicles[0].quantity} ${bookingPreview.vehicles[0].vehicleTypeName}`
    }
  }

  React.useEffect(() => {
    createNewBooking(quotedBooking)
    setVehicleInfo(getVehicleInfo())
  }, [])

  const getPassengerInfo = () => {
    if (newBooking) {
      if (newBooking.numberOfPassengers <= 1) {
        return newBooking.passengers.find((p: any) => p.isLeadPassenger).name
      } else {
        return `${
          newBooking.passengers.find((p: any) => p.isLeadPassenger).name
        } +  ${newBooking.numberOfPassengers - 1}`
      }
    }
  }

  function secondsToHms(d: number) {
    d = Number(d)
    var h = Math.floor(d / 3600)
    var m = Math.floor((d % 3600) / 60)

    var hDisplay = h > 0 ? h + (h == 1 ? ' hour ' : ' hours ') : ''
    var mDisplay = m > 0 ? m + (m == 1 ? ' min ' : ' mins ') : ''
    return hDisplay + mDisplay
  }

  return (
    <div>
      <BaseLayout pageSubtitle="We have received your booking" />
      {isLoading ? (
        <Grid
          container
          direction="row"
          justifyContent="center"
          alignItems="center"
          sx={styles.progressContainer}
        >
          <CircularProgress />
          <Typography variant="h5" gutterBottom sx={styles.progressTitle}>
            {`Getting your journey ready please wait...`}
          </Typography>
        </Grid>
      ) : (
        <Box component={'div'} sx={styles.root}>
          <div>
            {newBooking ? (
              <DirectionsMap
                origin={newBooking.outbound.dropOff}
                destination={newBooking.outbound.pickup}
                additionalStops={newBooking.outbound.additionalStops}
                mapHeight="250px"
                mapWidth="100%"
                mapOptions={{
                  zoomControl: false,
                  scrollWheelZoom: false,
                  dragging: false,
                  doubleClickZoom: false,
                  touchZoom: false,
                }}
              />
            ) : null}
          </div>

          <Grid container justifyContent="center">
            <Box component={'div'} sx={styles.infoGridContainer}>
              <Box component={'div'} sx={styles.infoContainerDark}>
                <Box component={'p'} sx={styles.infoHeader}>
                  BOOKING TYPE
                </Box>
                <Box component={'p'} sx={styles.infoDetail}>
                  {'Taxi'}
                </Box>
              </Box>
              <Box component={'div'} sx={styles.clientContainerDark}>
                <Box component={'p'} sx={styles.infoHeader}>
                  CLIENT
                </Box>
                <Box component={'p'} sx={styles.infoDetail}>
                  {clientName}
                </Box>
              </Box>
              <Box component={'div'} sx={styles.infoContainerDark}>
                <Box
                  component={'p'}
                  sx={(theme) => ({
                    ...styles.infoHeader,
                    color:
                      theme.components.bookingSummary.headerTextColour ||
                      styles.infoHeader.color,
                  })}
                >
                  EST JOURNEY TIME
                </Box>
                <Box component={'p'} sx={styles.infoDetail}>
                  {secondsToHms(
                    bookingConfirmation.outbound.estimatedJourneyTime,
                  )}
                </Box>
              </Box>
              <Box component={'div'} sx={styles.infoContainerDark}>
                <Box component={'p'} sx={styles.infoHeader}>
                  EST DISTANCE
                </Box>
                <Box
                  component={'p'}
                  sx={styles.infoDetail}
                >{`${bookingConfirmation.outbound.estimatedJourneyDistance} miles`}</Box>
              </Box>
              <Box component={'div'} sx={styles.infoContainerLight}>
                <Box component={'p'} sx={styles.infoHeader}>
                  PRICE
                </Box>
                <Box
                  component={'p'}
                  sx={styles.infoDetail}
                >{`£${bookingPreview.estimatedPrice}`}</Box>
              </Box>
            </Box>
            <Grid container justifyContent="center">
              <Grid item xs={4} sm={4}>
                <Box component={'div'} sx={styles.detailContainer}>
                  <p>
                    Thank you, Your booking has been received. Your Jyrney
                    reference is:
                  </p>
                  <h5>{bookingConfirmation.bookingReference}</h5>
                  <Divider sx={styles.detailDividerContainer} />
                  <p>We will pick you up at:</p>
                  <h4>
                    {newBooking
                      ? moment(newBooking.outbound.when).format(
                          'DD MMMM YYYY, h:mm a',
                        )
                      : ''}
                  </h4>
                  <Divider sx={styles.detailDividerContainer} />
                  <Box component={'div'} sx={styles.horizontalContainer}>
                    <ListItem>
                      <ListItemAvatar>
                        <Avatar>
                          <PeopleIcon />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        sx={{
                          primary: styles.journeyDetailListPrimaryText,
                          secondary: styles.journeyDetailListSecondaryText,
                        }}
                        primary="PASSENGER"
                        secondary={getPassengerInfo()}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemAvatar>
                        <Avatar>
                          <RoomIcon />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        sx={{
                          primary: styles.journeyDetailListPrimaryText,
                          secondary: styles.journeyDetailListSecondaryText,
                        }}
                        primary="FROM"
                        secondary={
                          newBooking
                            ? `${newBooking.outbound.pickup.addressLine1}, ${newBooking.outbound.pickup.town}`
                            : ''
                        }
                      />
                    </ListItem>
                  </Box>
                  <Box component={'div'} sx={styles.horizontalContainer}>
                    <ListItem>
                      <ListItemAvatar>
                        <Avatar>
                          <LocalTaxiIcon />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        sx={{
                          primary: styles.journeyDetailListPrimaryText,
                          secondary: styles.journeyDetailListSecondaryText,
                        }}
                        primary="VEHICLE"
                        secondary={vehicleInfo ? vehicleInfo : ''}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemAvatar>
                        <Avatar>
                          <RoomIcon />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        sx={{
                          primary: styles.journeyDetailListPrimaryText,
                          secondary: styles.journeyDetailListSecondaryText,
                        }}
                        primary="TO"
                        secondary={
                          newBooking
                            ? `${newBooking.outbound.dropOff.addressLine1}, ${newBooking.outbound.dropOff.town}`
                            : ''
                        }
                      />
                    </ListItem>
                  </Box>
                  <Divider />
                  <Box component={'div'} sx={styles.detailContainer}>
                    {newBooking && newBooking.isBookingOnBehalfOf ? (
                      <div>
                        <Box component={'p'} sx={styles.additionalInfoHeader}>
                          ON BEHALF OF
                        </Box>
                        <Box component={'p'} sx={styles.additionalInfoValue}>
                          {newBooking.onBehalfOf}
                        </Box>
                      </div>
                    ) : null}

                    {newBooking && newBooking.outbound.specialInstructions ? (
                      <div>
                        <Box component={'p'} sx={styles.additionalInfoHeader}>
                          SPECIAL INSTRUCTIONS
                        </Box>
                        <Box component={'p'} sx={styles.additionalInfoValue}>
                          {newBooking && newBooking.outbound.specialInstructions
                            ? newBooking.outbound.specialInstructions
                            : 'N/A'}
                        </Box>
                      </div>
                    ) : null}
                  </Box>
                  <Box component={'div'} sx={styles.actionContainer}>
                    {/*
                                        //@ts-ignore */}
                    <Button
                      color="error"
                      round={1}
                      size="medium"
                      onClick={cancelMyBooking}
                    >
                      CANCEL
                    </Button>
                    {/*
                                        //@ts-ignore */}
                    <Button
                      className={styles.actionButton}
                      color="warning"
                      round={1}
                      size="medium"
                      onClick={() =>
                        props.history.push('/admin/bookings/journeyType')
                      }
                    >
                      NEW BOOKING
                    </Button>
                  </Box>
                  <Box component={'p'} sx={styles.priceCaviat}>
                    Price is an estimate and may change due to (but not limited
                    to) the following reasons: change of route, added stops/pick
                    ups, tolls, parking, waiting time, lead in miles.
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Grid>
        </Box>
      )}
    </div>
  )
}

export default BookingConfirmationPage
